import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import {
  ChartBar,
  Archive,
  House,
  Wrench,
  FolderSimple,
  BookOpen,
  Plus,
  SignOut,
  CaretDown,
  UsersThree,
  Tag,
  Brain,
  Compass,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import LedgerSwitcher from "@/components/LedgerSwitcher";
import StewardshipDrawer from "@/components/StewardshipDrawer";

const navItems = [
  { to: "/", label: "Overview", icon: ChartBar, testid: "nav-dashboard", end: true },
  { to: "/assets", label: "Ledger", icon: Archive, testid: "nav-assets" },
  { to: "/home", label: "Home Components", icon: House, testid: "nav-home" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
  { to: "/vault", label: "Vault", icon: FolderSimple, testid: "nav-vault" },
  { to: "/transition", label: "Transition Guide", icon: Compass, testid: "nav-transition" },
  { to: "/help", label: "Help", icon: BookOpen, testid: "nav-help" },
];

export default function Layout() {
  const { user, logout, activeLedgerId, canWrite } = useAuth();
  const [stewardOpen, setStewardOpen] = useState(false);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen ledger-paper">
      {/* Top header bar */}
      <header
        className="sticky top-0 z-40 bg-[#F9F8F6]/95 backdrop-blur-sm border-b border-[#C9C4B8]"
        data-testid="topbar"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img
              src="/logo-mark.png"
              alt="The Asset Arbor"
              className="w-10 h-10 object-contain shrink-0"
              draggable="false"
            />
            <div className="flex flex-col leading-none">
              <span className="font-serif-display text-2xl text-[#0A192F]">The Asset Arbor</span>
              <span className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Est. 2026 · A Generational Ledger
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setStewardOpen(true)}
              data-testid="open-stewardship-btn"
              title="Ask Leo — your private estate analyst"
              className="hidden sm:inline-flex items-center gap-2 bg-white border border-[#E0DDD6] hover:border-[#1A3622] px-3 py-2 text-sm text-[#0A192F] transition-colors"
            >
              <Brain size={14} weight="regular" className="text-[#1A3622]" />
              <span className="hidden md:inline">Ask Leo</span>
            </button>
            <LedgerSwitcher />
            {canWrite && (
              <Link
                to="/assets/new"
                data-testid="header-add-asset-btn"
                className="hidden sm:inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] transition-colors"
              >
                <Plus size={16} weight="bold" />
                Record Asset
              </Link>
            )}

            {/* User menu */}
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="user-menu-btn"
                className="inline-flex items-center gap-2 bg-white border border-[#E0DDD6] hover:border-[#1A3622] px-2 py-1.5 text-sm text-[#0A192F]"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 object-cover" />
                ) : (
                  <span className="w-7 h-7 bg-[#1A3622] text-[#F9F8F6] flex items-center justify-center font-serif-display">
                    {initials}
                  </span>
                )}
                <span className="hidden md:inline font-mono-ledger text-xs max-w-[140px] truncate">
                  {user?.name || user?.email}
                </span>
                <CaretDown size={12} />
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-white border border-[#E0DDD6] shadow-sm z-50"
                  data-testid="user-menu"
                >
                  <div className="px-4 py-3 border-b border-[#E0DDD6]">
                    <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                      Signed in as
                    </div>
                    <div className="text-sm text-[#0A192F] truncate font-medium">
                      {user?.name || user?.email?.split("@")[0]}
                    </div>
                    <div className="text-xs text-[#5C5F58] truncate">{user?.email}</div>
                    {user?.role === "admin" && (
                      <div className="mt-2 inline-block px-2 py-0.5 bg-[#0A192F] text-[#F9F8F6] text-[10px] font-mono-ledger uppercase tracking-[0.2em]">
                        Admin
                      </div>
                    )}
                  </div>
                  <Link
                    to="/households"
                    onClick={() => setMenuOpen(false)}
                    data-testid="user-menu-households-link"
                    className="w-full text-left px-4 py-3 text-sm text-[#0A192F] hover:bg-[#F9F8F6] inline-flex items-center gap-2 border-b border-[#E0DDD6]"
                  >
                    <UsersThree size={14} />
                    Households
                  </Link>
                  <Link
                    to="/settings/classes"
                    onClick={() => setMenuOpen(false)}
                    data-testid="user-menu-classes-link"
                    className="w-full text-left px-4 py-3 text-sm text-[#0A192F] hover:bg-[#F9F8F6] inline-flex items-center gap-2 border-b border-[#E0DDD6]"
                  >
                    <Tag size={14} />
                    Asset Classes
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="w-full text-left px-4 py-3 text-sm text-[#0A192F] hover:bg-[#F9F8F6] inline-flex items-center gap-2"
                  >
                    <SignOut size={14} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-[240px_1fr]">
        {/* Sidebar */}
        <aside className="hidden md:block border-r border-[#E0DDD6] bg-[#F9F8F6] min-h-[calc(100vh-64px)]">
          <nav className="p-6 space-y-1" data-testid="sidebar-nav">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-4">
              Folio
            </div>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                      isActive
                        ? "border-l-[#1A3622] bg-[#FFFFFF] text-[#0A192F] font-medium"
                        : "border-l-transparent text-[#5C5F58] hover:text-[#0A192F] hover:bg-[#FFFFFF]/60"
                    }`
                  }
                >
                  <Icon size={18} weight="regular" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="mx-6 mt-10 p-4 border border-[#E0DDD6] bg-white">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
              Principle
            </div>
            <p className="font-serif-display text-lg leading-snug text-[#0A192F]">
              &quot;Intelligent Ownership.<br />Enduring Legacy.&quot;
            </p>
          </div>
        </aside>

        {/* Mobile bottom nav */}
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-7 bg-white border-t border-[#C9C4B8]"
          data-testid="mobile-nav"
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2 gap-1 text-[10px] ${
                    isActive ? "text-[#1A3622]" : "text-[#5C5F58]"
                  }`
                }
              >
                <Icon size={18} weight="regular" />
                {item.label.split(" ")[0]}
              </NavLink>
            );
          })}
        </nav>

        {/* Main content */}
        <main className="min-w-0 px-6 md:px-10 py-8 md:py-10 pb-24 md:pb-10" data-testid="main-content">
          <Outlet key={activeLedgerId || "personal"} />
        </main>
      </div>

      {/* Mobile FAB — Stewardship */}
      <button
        onClick={() => setStewardOpen(true)}
        data-testid="open-stewardship-fab"
        title="Ask Leo"
        aria-label="Ask Leo"
        className="sm:hidden fixed bottom-20 right-4 z-40 w-12 h-12 bg-[#1A3622] text-[#F9F8F6] border border-[#0F2818] shadow-lg flex items-center justify-center hover:bg-[#0F2818]"
      >
        <Brain size={20} weight="regular" />
      </button>

      <StewardshipDrawer open={stewardOpen} onOpenChange={setStewardOpen} />
    </div>
  );
}
